import React, { useState, useEffect } from 'react';
import { 
  VideoCameraIcon, 
  PlayIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { extractZoomMeetingId, extractZoomPassword, extractZoomRecordingId, isZoomRecording, extractZoomUrlParams, generateZoomEmbedUrl, canEmbedZoomUrl } from '../utils/videoUtils';

interface ZoomPlayerProps {
  videoUrl: string;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

const ZoomPlayer: React.FC<ZoomPlayerProps> = ({
  videoUrl,
  onProgress,
  onComplete,
  onTimeUpdate
}) => {
  const [isWatching, setIsWatching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [meetingInfo, setMeetingInfo] = useState<{
    meetingId: string | null;
    recordingId: string | null;
    password: string | null;
    isJoinable: boolean;
    isRecording: boolean;
    additionalParams: { [key: string]: string };
  }>({
    meetingId: null,
    recordingId: null,
    password: null,
    isJoinable: false,
    isRecording: false,
    additionalParams: {}
  });

  useEffect(() => {
    const isRecording = isZoomRecording(videoUrl);
    const meetingId = extractZoomMeetingId(videoUrl);
    const recordingId = extractZoomRecordingId(videoUrl);
    const password = extractZoomPassword(videoUrl);
    const additionalParams = extractZoomUrlParams(videoUrl);
    
    setMeetingInfo({
      meetingId,
      recordingId,
      password,
      isJoinable: Boolean(meetingId || recordingId),
      isRecording,
      additionalParams
    });
  }, [videoUrl]);

  const handleWatch = () => {
    setIsWatching(true);
    
    // Auto-copy passcode to clipboard if available
    if (meetingInfo.password) {
      copyToClipboard(meetingInfo.password, 'password');
    }
    
    if (onProgress) {
      onProgress(25); // Mark as started when user clicks watch
    }
  };

  const handleMarkComplete = () => {
    if (onProgress) {
      onProgress(100);
    }
    if (onComplete) {
      onComplete();
    }
  };

  const copyToClipboard = async (text: string, itemName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(null), 2000);
    }
  };

  return (
    <div className="space-y-4">
      {/* Zoom Meeting Card */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <VideoCameraIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {meetingInfo.isRecording ? 'Zoom Recording' : 'Zoom Meeting'}
              </h3>
              <p className="text-blue-100 text-sm">
                {meetingInfo.isRecording ? 'Cloud recording from Zoom' : 'Live or recorded meeting session'}
              </p>
            </div>
          </div>
        </div>

        {/* Meeting Info */}
        <div className="p-4 space-y-4">

          {/* Fallback for URLs without extractable IDs */}
          {!(meetingInfo.meetingId || meetingInfo.recordingId) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Zoom URL Detected</p>
                  <p className="mb-2">This appears to be a Zoom link, but we couldn't extract the meeting details automatically.</p>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-xs text-gray-600 font-mono break-all">{videoUrl}</p>
                  </div>
                </div>
              </div>
            </div>
          )}


           {/* Video Display */}
           {isWatching ? (
             <div className="space-y-4">
               {/* Meeting Details for Easy Copy */}
               <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                 <div className="flex items-center space-x-2 mb-3">
                   <LinkIcon className="h-5 w-5 text-blue-600" />
                   <h4 className="font-medium text-blue-900">Meeting Details</h4>
                 </div>
                 

                 {/* Passcode */}
                 {meetingInfo.password && (
                   <div>
                     <label className="block text-sm font-medium text-green-800 mb-1">
                       Meeting Passcode
                     </label>
                     <div className="flex items-center space-x-3">
                       <input
                         type="text"
                         value={meetingInfo.password}
                         readOnly
                         className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-lg font-mono text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                         onClick={(e) => e.currentTarget.select()}
                       />
                       <button
                         onClick={() => copyToClipboard(meetingInfo.password || '', 'password')}
                         className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                       >
                         <ClipboardDocumentIcon className="h-4 w-4" />
                         <span className="text-sm font-medium">
                           {copiedItem === 'password' ? 'Copied!' : 'Copy'}
                         </span>
                       </button>
                     </div>
                   </div>
                 )}

               </div>

               {/* Embedded Video */}
               <div className="bg-gray-900 rounded-lg overflow-hidden">
                 <div className="p-3 bg-gray-800 flex items-center justify-between">
                   <div className="flex items-center space-x-2">
                     <VideoCameraIcon className="h-5 w-5 text-white" />
                     <span className="text-white font-medium">
                       {meetingInfo.isRecording ? 'Recording' : 'Meeting'}
                     </span>
                   </div>
                   <button
                     onClick={handleMarkComplete}
                     className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                   >
                     Mark Complete
                   </button>
                 </div>
                 <iframe
                   src={generateZoomEmbedUrl(videoUrl)}
                   className="w-full h-96 md:h-[500px]"
                   frameBorder="0"
                   allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                   allowFullScreen
                   title={meetingInfo.isRecording ? 'Zoom Recording' : 'Zoom Meeting'}
                   onLoad={() => {
                     if (onProgress) {
                       onProgress(50);
                     }
                   }}
                 />
               </div>
             </div>
          ) : (
            <div className="text-center py-8">
              <button
                onClick={handleWatch}
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-bold text-lg rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
              >
                <PlayIcon className="h-6 w-6 mr-3" />
                Watch {meetingInfo.isRecording ? 'Recording' : 'Meeting'}
              </button>
            </div>
          )}


          {/* Progress Indicator */}
          {isWatching && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm text-gray-500">
                  {isWatching ? '50%' : '0%'} completed
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: isWatching ? '50%' : '0%' }}
                ></div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ZoomPlayer;
